<script lang="ts">
  import {
    Button,
    Card,
    Divider,
    Icon,
    TierBadge,
    type Language,
  } from '@bruce/components'
  import { utils } from '$lib/utils'
  import { page } from '$app/stores'
  import type { BookingMethodsSubscription } from '@bruce/api/common/bookingMethods'
  import { STRINGS } from '$lib/constants/strings'
  import type { User } from '@bruce/api/common/user'
  import type { CityCampaign } from '$lib/types/campaign'
  import type { CityIncludesWithToken } from '@bruce/api/api/location/city/response'
  import type { ContractType } from '$lib/types/contract'

  export let subscription: BookingMethodsSubscription
  export let currency: string
  export let trial: boolean
  export let user: User | undefined = undefined
  export let contractType: ContractType
  export let campaign: CityCampaign | undefined = undefined
  export let currentCity:
    | CityIncludesWithToken<{
        params: {
          include_country: true
          include_booking_methods: true
          locale: Language
        }
      }>
    | undefined = undefined

  $: subscriptionTitleCase = utils.toTitleCase(
    subscription.subscription_info.tier_name,
  )

  $: lang = $page.params.lang as Language
  $: localeRoute = utils.getRoute(lang)

  $: showAnnual =
    contractType === 'annual' &&
    subscription.subscription_info.tier_name === 'BASE'

  $: selectTierLinkSearchParams = new URLSearchParams(
    utils.object.filterObjectValues(
      {
        tier: subscription.subscription_info.tier_name.toLowerCase(),
        'contract-type': contractType,
        'campaign-code': campaign?.tierName?.includes(
          utils.toTierName(subscription.subscription_info.tier_name),
        )
          ? campaign?.code
          : undefined,
      },
      (value) => value !== undefined,
    ),
  )

  $: showSavings =
    campaign?.tierName?.includes(
      utils.toTierName(subscription.subscription_info.tier_name),
    ) || showAnnual

  function getSavings() {
    let savings = 0
    if (
      campaign?.tierName?.includes(
        utils.toTierName(subscription.subscription_info.tier_name),
      )
    ) {
      if (campaign.discount) {
        savings =
          subscription.subscription_info.flex_price -
          Math.floor(
            (subscription.subscription_info.flex_price * campaign.discount) /
              100,
          ) *
            100 // Always round down cents
      } else if (campaign.absoluteDiscount) {
        savings = campaign.absoluteDiscount * 100
      } else if (campaign.price) {
        savings =
          subscription.subscription_info.flex_price - campaign.price * 100
      }
    } else if (showAnnual) {
      savings =
        (subscription.subscription_info.flex_price -
          subscription.subscription_info.commit_price) *
        12
    }

    return utils.formatCurrency({
      price: savings,
      currency,
      locale: `${lang}-${currentCity?.country?.code || 'SE'}`,
      hideDecimals: true,
    })
  }

  function getPrice() {
    const locale = `${lang}-${currentCity?.country?.code || 'SE'}`

    if (
      campaign?.discount &&
      campaign?.tierName?.includes(
        utils.toTierName(subscription.subscription_info.tier_name),
      )
    ) {
      return utils.formatCurrency({
        price:
          Math.floor(
            (subscription.subscription_info.flex_price * campaign.discount) /
              100,
          ) * 100, // Always round down cents
        currency,
        locale,
        hideDecimals: true,
      })
    } else if (
      campaign?.absoluteDiscount &&
      campaign?.tierName?.includes(
        utils.toTierName(subscription.subscription_info.tier_name),
      )
    ) {
      return utils.formatCurrency({
        price:
          Math.floor(
            (subscription.subscription_info.flex_price -
              campaign.absoluteDiscount * 100) /
              100,
          ) * 100, // Always round down cents
        currency,
        locale,
        hideDecimals: true,
      })
    } else if (
      campaign?.price &&
      campaign?.tierName?.includes(
        utils.toTierName(subscription.subscription_info.tier_name),
      )
    ) {
      return utils.formatCurrency({
        price: campaign.price * 100,
        currency,
        locale,
        hideDecimals: true,
      })
    } else if (showAnnual) {
      return utils.formatCurrency({
        price: subscription.subscription_info.commit_price,
        currency,
        locale,
        hideDecimals: true,
      })
    } else {
      return utils.formatCurrency({
        price: subscription.subscription_info.flex_price,
        currency,
        locale,
        hideDecimals: true,
      })
    }
  }

  function scrollFaqIntoView() {
    const faqElement = document.querySelector('section.faq')
    faqElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
</script>

<Card block>
  <div class="container">
    <div class="title">
      <div class="tier">
        <TierBadge
          tier={utils.toTierName(subscription.subscription_info.tier_name)}
        />
      </div>
      {#if showSavings}
        <div class="savings">
          {STRINGS[lang].MEMBERSHIP_CARD_SAVE}
          {getSavings()}
        </div>
      {/if}
    </div>
    <div class="price">
      {#if showSavings}
        <div class="old-price">
          {utils.formatCurrency({
            price: subscription.subscription_info.flex_price,
            currency,
            locale: `${lang}-${currentCity?.country?.code || 'SE'}`,
            hideDecimals: true,
          })}
        </div>
      {/if}
      <div class="price-currency" class:discounted={showSavings}>
        {getPrice()}
      </div>
      / {STRINGS[lang].MONTH_SHORT}
    </div>
    <p class="description">{utils.balanceText(subscription.description)}</p>
    {#if subscription.usps}
      <ul>
        {#each subscription.usps as usp, index (usp.id)}
          <li>
            {#if usp.destination}
              <a
                href={localeRoute(
                  usp.destination.type === 'EXCLUSIVE'
                    ? `/studios?filter=${usp.id}`
                    : `/studios?tier=${subscription.subscription_info.tier_name.toLowerCase()}`,
                )}
              >
                <Icon icon="ph-seal-check-fill" />
                <div>
                  {usp.title}
                </div>
                {#if usp.description}
                  <div class="description">{usp.description}</div>
                {/if}
                <div class="right-arrow">
                  <Icon icon="ph-caret-right" />
                </div>
              </a>
            {:else}
              <div class="usp">
                <Icon icon="ph-seal-check-fill" />
                <div>
                  {usp.title}
                </div>
                {#if usp.description}
                  <div class="description">{usp.description}</div>
                {/if}
              </div>
            {/if}
          </li>
          {#if index < subscription.usps.length - 1}
            <Divider />
          {/if}
        {/each}
      </ul>
    {/if}
    <div class="buttons">
      {#if trial && subscription.subscription_info.tier_name === 'BLACK'}
        <Button rounded href={localeRoute('/trial')} block variant="flat">
          {STRINGS[lang].TRIAL_BUTTON}
        </Button>
        <Button
          rounded
          href={localeRoute(
            user
              ? `/checkout?${selectTierLinkSearchParams}`
              : `/signup?${selectTierLinkSearchParams}`,
          )}
          block
          variant="outlined"
        >
          {STRINGS[lang].MEMBERSHIP_CARD_SELECT}
          {subscriptionTitleCase}
          {contractType === 'annual'
            ? STRINGS[lang].ANNUAL
            : STRINGS[lang].MONTHLY}
        </Button>
      {:else}
        <Button
          rounded
          href={localeRoute(
            user
              ? `/checkout?${selectTierLinkSearchParams}`
              : `/signup?${selectTierLinkSearchParams}`,
          )}
          block
          variant="flat"
        >
          {STRINGS[lang].MEMBERSHIP_CARD_SELECT}
          {subscriptionTitleCase}
          {contractType === 'annual'
            ? STRINGS[lang].ANNUAL
            : STRINGS[lang].MONTHLY}
        </Button>
      {/if}
    </div>
    {#if contractType === 'annual'}
      <div class="annual-disclaimer">
        <div>
          {STRINGS[lang].MEMBERSHIP_CARD_ANNUAL_FAQ}
          <button on:click={scrollFaqIntoView}>
            {STRINGS[lang].MEMBERSHIP_CARD_ANNUAL_FAQ_LINK}</button
          >.
        </div>
        <div>{STRINGS[lang].MEMBERSHIP_CARD_ANNUAL_DISCLAIMER}</div>
      </div>
    {/if}
  </div>
</Card>

<style lang="scss">
  .container {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 400px;

    .title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1em;

      .tier {
        font-size: 20px;
      }

      .savings {
        background-color: var(--savings-background);
        color: var(--savings-text-color);
        padding: 0.5em 1em;
        border-radius: 2em;
        font-size: 14px;
        font-weight: 500;
      }
    }

    .price {
      display: flex;
      align-items: end;
      font-size: 16px;
      font-weight: bold;

      .price-currency,
      .old-price {
        font-size: 24px;
        margin-bottom: -3px;
      }

      .price-currency {
        margin-right: 0.25em;

        &.discounted {
          color: var(--savings-text-color);
        }
      }

      .old-price {
        font-weight: normal;
        text-decoration: line-through;
        margin-right: 0.5em;
      }
    }

    .price,
    .description,
    .annual-disclaimer {
      margin-left: 0.5em;
    }

    .description {
      font-size: 16px;
    }

    ul {
      display: flex;
      flex-direction: column;
      gap: 1em;
      list-style-type: none;
      padding: 0;
      margin: 1em 0 2em 0;

      li > a,
      li > .usp {
        display: grid;
        align-items: center;
        column-gap: 0.5em;
        row-gap: 0.25em;
        --icon-color: var(--usp-icon-color);

        .description {
          font-size: 14px;
          grid-column: 2;
        }
      }

      li > a {
        text-decoration: none;
        grid-template-columns: max-content 1fr max-content;

        &:hover {
          text-decoration: underline;
        }

        .right-arrow {
          --icon-color: var(--color-on-surface);
          grid-column: 3;
          grid-row: 1 / span 2;
          justify-self: end;
        }
      }

      li > .usp {
        grid-template-columns: max-content 1fr;
      }
    }

    .buttons {
      display: flex;
      flex-direction: column;
      gap: 1em;
      margin-top: auto;
    }

    .annual-disclaimer {
      display: flex;
      flex-direction: column;
      text-align: center;
      gap: 1em;
      margin-top: 1em;
      font-size: 14px;
      opacity: 0.6;

      button {
        appearance: none;
        border: none;
        background: transparent;
        padding: 0;
        font-size: inherit;
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }
</style>
